import React from 'react';
import PropTypes from 'prop-types';
import { MapPin, User, Clock, DollarSign, MessageCircle } from 'lucide-react';

const OpportunityCard = ({ opportunity, onViewDetails, onMessage, isMessagingWithAdvisor = false }) => {
  const getPositionTypeBadge = () => {
    if (opportunity.position_type === 'full-time' || opportunity.position_type === 'full_time') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Full-Time
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
        Internship
      </span>
    );
  };

  const getRemoteBadge = () => {
    if (opportunity.remote) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Remote
        </span>
      );
    }
    return null;
  };

  const formatPostedDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200 relative">
      {/* Messaging Indicator - Top Right */}
      {isMessagingWithAdvisor && (
        <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full">
          <MessageCircle className="h-3.5 w-3.5 text-blue-600" />
          <span className="text-xs font-medium text-blue-700">In conversation</span>
        </div>
      )}

      {/* Header with badges */}
      <div className="flex items-center gap-2 mb-3">
        {getPositionTypeBadge()}
        {getRemoteBadge()}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold mb-3 text-gray-900 line-clamp-2 min-h-[3.5rem]">
        {opportunity.title}
      </h3>

      {/* Advisor Info */}
      <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
        <User className="h-4 w-4 flex-shrink-0 text-gray-400" />
        <span className="truncate">
          {opportunity.advisor_display_name || opportunity.user?.name || 'Advisor'}
        </span>
      </div>

      {/* Location */}
      <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
        <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" />
        <span className="truncate">{opportunity.location}</span>
      </div>

      {/* Duration & Compensation Row */}
      <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
        {opportunity.duration && (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <span>{opportunity.duration}</span>
          </div>
        )}
        {opportunity.compensation && (
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <span>{opportunity.compensation}</span>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-3 min-h-[3.75rem]">
        {opportunity.description || 'No description available.'}
      </p>

      {/* Posted Date */}
      <p className="text-xs text-gray-500 mb-4">
        Posted {formatPostedDate(opportunity.published_at)}
      </p>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => onViewDetails && onViewDetails(opportunity.slug || opportunity.id)}
          className="flex-1 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
        >
          View Details →
        </button>
      </div>
    </div>
  );
};

OpportunityCard.propTypes = {
  opportunity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    slug: PropTypes.string,
    title: PropTypes.string.isRequired,
    position_type: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    remote: PropTypes.bool,
    description: PropTypes.string,
    compensation: PropTypes.string,
    duration: PropTypes.string,
    published_at: PropTypes.string,
    advisor_display_name: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  }).isRequired,
  onViewDetails: PropTypes.func,
  onMessage: PropTypes.func,
  isMessagingWithAdvisor: PropTypes.bool,
};

export default OpportunityCard;

